import { useQuery } from '@tanstack/react-query';
import apiClient from '../../../../lib/apiClient';

/**
 * MITRE ATT&CK matrix structure — 14 tactics + 656 active techniques +
 * tactic↔technique links. Slow-changing reference data; cache for 30 min.
 */
export function useMitreMatrix() {
  return useQuery({
    queryKey: ['mitre', 'matrix'],
    queryFn: () => apiClient.get('/api/mitre/matrix'),
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * List of vendors that have MITRE coverage data — drives the dropdown.
 */
export function useMitreVendors() {
  return useQuery({
    queryKey: ['mitre', 'vendors'],
    queryFn: () => apiClient.get('/api/mitre/vendors'),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Per-technique coverage rollup. Two modes:
 *   - Vendor mode:     pass { vendor: 'Microsoft' }
 *   - Department mode: pass { departmentId: 39 } (union of all owned vendors)
 *
 * Returns the same shape from both endpoints (`coverage[]` rows). In
 * department mode each row also carries `contributing_vendors[]`.
 *
 * Disabled when neither input is provided so we don't fire a request for
 * the "nothing selected yet" state.
 */
export function useMitreCoverage({ vendor, departmentId } = {}) {
  const queryParam = vendor
    ? `vendor=${encodeURIComponent(vendor)}`
    : departmentId
    ? `department_id=${departmentId}`
    : null;

  return useQuery({
    queryKey: ['mitre', 'coverage', vendor || `dept-${departmentId}` || '__none__'],
    queryFn: () => apiClient.get(`/api/mitre/coverage?${queryParam}`),
    enabled: !!queryParam,
    staleTime: 5 * 60 * 1000,
  });
}
